import api from './axiosApi'

export interface PlanItem {
  id: string
  skuNumber: string
  description: string
}

export interface PlanItemsResponse {
  items: PlanItem[]
  totalCount: number
  page: number
  pageSize: number
  totalPages: number
}

// Backend types for version lines endpoint
interface VersionLineItemRead {
  id: string
  version_id: string
  line_number: number
  sku_number: string | null
  description: string | null
  division: string | null
  department: string | null
  class: string | null
  subclass: string | null
  vendor_id: string | null
  vendor_name: string | null
  target_retail: string | null
  target_cost: string | null
  target_margin: string | null
  ty_plan_units: number | null
  workflow_status: string | null
  created_at: string
  updated_at: string
}

interface PaginationMeta {
  skip: number
  limit: number
  total: number
  has_more: boolean
}

interface PaginatedVersionLinesResponse {
  data: VersionLineItemRead[]
  pagination: PaginationMeta
}

// Plan details response type
export interface PlanDetails {
  id: string
  name: string
  active_version_id: string
  versions_count: number
}

// Map backend VersionLineItemRead to frontend PlanItem
function mapVersionLineItemToPlanItem(item: VersionLineItemRead): PlanItem {
  return {
    id: item.id,
    skuNumber: item.sku_number ?? '-',
    description: item.description ?? '',
  }
}

/**
 * Get plan details to retrieve active_version_id
 * @param planId - The plan ID to fetch details for
 * @returns Promise with plan details including active_version_id
 */
export async function getPlanDetails(planId: string): Promise<PlanDetails> {
  const response = await api.get<PlanDetails>(`/plans/${planId}`)
  return response.data
}

/**
 * Get paginated items for a plan version
 * @param planId - The plan ID
 * @param versionId - The version ID to fetch items for
 * @param page - Page number (1-indexed)
 * @param pageSize - Number of items per page
 * @returns Promise with paginated items
 */
export async function getPlanVersionLines(
  planId: string,
  versionId: string,
  page: number = 1,
  pageSize: number = 5
): Promise<PlanItemsResponse> {
  // Convert page/pageSize to offset/limit
  const offset = (page - 1) * pageSize

  const response = await api.get<PaginatedVersionLinesResponse>(
    `/plans/${planId}/versions/${versionId}/lines`,
    { params: { offset, limit: pageSize } }
  )

  const totalCount = response.data.pagination.total
  const totalPages = Math.ceil(totalCount / pageSize)

  return {
    items: response.data.data.map(mapVersionLineItemToPlanItem),
    totalCount,
    page,
    pageSize,
    totalPages,
  }
}
